import { useState, useRef, useEffect } from 'react';
import { useSidebar } from '../contexts/SidebarContext';
import { getProducts, deleteSupplierInterior } from '../api/supplierInteriors';

/**
 * Supplier Component
 * Supplier page with image upload (different process from StartDesigning).
 * "Let's Begin" navigates to /upload-material with the uploaded image.
 */

const Supplier = () => {
  const { setCurrentView, setSupplierUploadedImage, setSupplierUploadedFile } = useSidebar();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);
  const [productOverflowMenuId, setProductOverflowMenuId] = useState(null);
  const [productDeletingId, setProductDeletingId] = useState(null);

  useEffect(() => {
    setProductsLoading(true);
    setProductsError(null);
    getProducts(1, 10)
      .then((res) => {
        setProducts(Array.isArray(res.products) ? res.products : []);
      })
      .catch((err) => {
        console.error('Failed to load products:', err);
        setProductsError(err?.message ?? 'Failed to load products');
      })
      .finally(() => setProductsLoading(false));
  }, []);

  useEffect(() => {
    if (productOverflowMenuId == null) return;
    const close = (e) => {
      if (!e.target.closest('[data-supplier-product-overflow]')) {
        setProductOverflowMenuId(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [productOverflowMenuId]);

  const handleDeleteProduct = async (p) => {
    const label = p.name || `Product #${p.id}`;
    if (
      !window.confirm(
        `Delete "${label}"? This permanently removes this product on the server and cannot be undone.`
      )
    ) {
      return;
    }
    setProductOverflowMenuId(null);
    setProductsError(null);
    setProductDeletingId(p.id);
    try {
      await deleteSupplierInterior(p.id);
      setProducts((prev) => prev.filter((prod) => prod.id !== p.id));
    } catch (err) {
      console.error('Failed to delete supplier product:', err);
      setProductsError(err?.message ?? 'Failed to delete product');
    } finally {
      setProductDeletingId(null);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file) => {
    setUploadError(null);
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Please upload a JPG, PNG, or PDF file');
      return;
    }
    const maxSize = 8 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('File size must be less than 8MB');
      return;
    }
    setUploadedFile(file);
    setSupplierUploadedFile(file);
  };

  const clearFile = () => {
    setUploadedFile(null);
    setSupplierUploadedFile(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleLetsBegin = () => {
    if (!uploadedFile) return;
    const isImage = uploadedFile.type.startsWith('image/');
    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSupplierUploadedImage(e.target.result);
        setCurrentView('UploadMaterial');
      };
      reader.readAsDataURL(uploadedFile);
    } else {
      setSupplierUploadedImage(null);
      setCurrentView('UploadMaterial');
    }
  };

  return (
    <div className="w-full">
      <h1 className="text-4xl font-bold text-gray-800 mb-8">Supplier Products</h1>

      <div className="grid grid-cols-1 gap-6 mb-8">
        {/* Image upload block */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 transition-colors ${
            isDragging
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 bg-gray-50 hover:border-gray-400'
          }`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center justify-center text-center h-full min-h-[400px]">
            {uploadedFile ? (
              <>
                <div className="mb-4">
                  <svg
                    className="w-16 h-16 text-green-500 mx-auto"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-lg font-semibold text-gray-800 mb-2">File uploaded</p>
                <p className="text-sm text-gray-600 mb-4">{uploadedFile.name}</p>
                <div className="flex flex-col items-center gap-3">
                  <button
                    type="button"
                    onClick={handleLetsBegin}
                    className="bg-gray-800 hover:bg-gray-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
                  >
                    Let's Begin
                  </button>
                  <button
                    type="button"
                    onClick={clearFile}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Remove file
                  </button>
                </div>
              </>
            ) : (
              <>
                <svg
                  className="w-16 h-16 text-gray-400 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <p className="text-lg font-semibold text-gray-800 mb-2">Upload Product Image Here</p>
              </>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileInput}
              className="hidden"
            />

            {uploadError && (
              <p className="text-sm text-red-600 mb-2">{uploadError}</p>
            )}

            {!uploadedFile && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-8 rounded-lg transition-colors"
              >
                Upload file
              </button>
            )}

            <div className="mt-6 text-center text-xs text-gray-500">
              <p>Supported files: JPG, PNG, PDF</p>
              <p>Maximum file size: 8MB</p>
            </div>
          </div>
        </div>
      </div>

      {/* Existing products list */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Your products</h2>
          {productsLoading && (
            <span className="text-xs text-gray-500">Loading products…</span>
          )}
        </div>
        {productsError && (
          <p className="text-sm text-red-600 mb-4">{productsError}</p>
        )}
        {products.length === 0 && !productsLoading && !productsError ? (
          <p className="text-sm text-gray-500">No products found.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <div
                key={p.id}
                className={`relative border border-gray-200 rounded-lg bg-white shadow-sm flex flex-col ${
                  productDeletingId === p.id ? 'opacity-60 pointer-events-none' : ''
                }`}
              >
                <div className="absolute top-2 right-2 z-10" data-supplier-product-overflow>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setProductOverflowMenuId((prev) => (prev === p.id ? null : p.id));
                    }}
                    disabled={productDeletingId != null}
                    className={`rounded-full p-1.5 transition-colors ${
                      productOverflowMenuId === p.id
                        ? 'bg-white/95 text-gray-800 shadow border border-gray-200'
                        : 'bg-white/90 text-gray-600 hover:text-gray-900 shadow-sm border border-gray-200/80'
                    }`}
                    title="More actions"
                    aria-expanded={productOverflowMenuId === p.id}
                    aria-haspopup="true"
                    aria-label={`More actions for ${p.name || `product ${p.id}`}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <circle cx="12" cy="5" r="1.75" />
                      <circle cx="12" cy="12" r="1.75" />
                      <circle cx="12" cy="19" r="1.75" />
                    </svg>
                  </button>
                  {productOverflowMenuId === p.id && (
                    <div
                      role="menu"
                      className="absolute right-0 mt-1 min-w-[10rem] rounded-lg border border-gray-200 bg-white py-1 shadow-lg z-20"
                    >
                      <button
                        type="button"
                        role="menuitem"
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDeleteProduct(p);
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0" aria-hidden>
                          <path d="M3 6h18" />
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                          <line x1="10" y1="11" x2="10" y2="17" />
                          <line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                <div className="h-40 bg-gray-100 flex items-center justify-center overflow-hidden rounded-t-lg">
                  {p.images ? (
                    <img
                      src={p.images}
                      alt={p.name || 'Product image'}
                      className="w-full h-full object-cover"
                      crossOrigin="anonymous"
                      onError={(e) => {
                        // If the main product image fails (e.g., expired/forbidden URL),
                        // clear it so we fall back to the textual "No preview" state.
                        e.currentTarget.onerror = null;
                        setProducts((prev) =>
                          prev.map((prod) =>
                            prod.id === p.id ? { ...prod, images: null } : prod
                          )
                        );
                      }}
                    />
                  ) : (
                    <span className="text-xs text-gray-400">No preview</span>
                  )}
                </div>
                <div className="p-3 space-y-1 rounded-b-lg bg-white">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {p.name || `Product #${p.id}`}
                  </p>
                  {p.interiorType?.interiorType && (
                    <p className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-[11px] font-medium text-gray-700">
                      {p.interiorType.interiorType}
                    </p>
                  )}
                  {p.description?.text && (
                    <p className="text-xs text-gray-600 line-clamp-2">
                      {p.description.text}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    ID: {p.id}{' '}
                    {typeof p.supplierId === 'number' && (
                      <span className="ml-1">• Supplier {p.supplierId}</span>
                    )}
                  </p>
                  {p.createdAt && (
                    <p className="text-xs text-gray-400">
                      Created: {new Date(p.createdAt).toLocaleString()}
                    </p>
                  )}
                  {Array.isArray(p.materials) && p.materials.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-[11px] font-semibold text-gray-700">Materials</p>
                      <div className="flex flex-wrap gap-2">
                        {p.materials.map((mat) => (
                          <div
                            key={mat.id}
                            className="flex items-center gap-2 px-2 py-1 rounded-md border border-gray-200 bg-gray-50"
                          >
                            {mat.imageKey && (
                              <img
                                src={mat.imageKey}
                                alt={mat.materialType || 'Material'}
                                className="w-6 h-6 rounded object-cover"
                                crossOrigin="anonymous"
                                onError={(e) => {
                                  // If a material thumbnail fails, clear its imageKey
                                  // so we show only the text label without a broken icon.
                                  e.currentTarget.onerror = null;
                                  setProducts((prev) =>
                                    prev.map((prod) =>
                                      prod.id === p.id
                                        ? {
                                            ...prod,
                                            materials: Array.isArray(prod.materials)
                                              ? prod.materials.map((m) =>
                                                  m.id === mat.id ? { ...m, imageKey: null } : m
                                                )
                                              : prod.materials,
                                          }
                                        : prod
                                    )
                                  );
                                }}
                              />
                            )}
                            <div className="flex flex-col">
                              <span className="text-[11px] font-medium text-gray-800">
                                {mat.materialType || `Material #${mat.id}`}
                              </span>
                              <span className="text-[10px] text-gray-500">ID: {mat.id}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Supplier;
